import { saveAs } from 'file-saver';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NAV_HEIGHT } from '~/common/constants/page';
import { MIN_SEARCH_LENGTH } from '~/common/constants/search';
import { getUsersConstans } from '~/common/constants/users';
import { ImportUserStatus } from '~/components/Admin/ImportUserStatus/ImportUserStatus';
import { ImportUsersWizard } from '~/components/Admin/ImportUsersWizard/ImportUsersWizard';
import { exportUsersExcelFile } from '~/components/Admin/UsersPage/UsersPage.data';
import { trackSegment } from '~/components/SegmentAnalytics';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { DeactivateUserModal } from '~/components/Users/DeactivateUserModal';
import { ResetUserPasswordModal } from '~/components/Users/ResetUserPasswordModal';
import { UserFormPanel } from '~/components/Users/UserFormPanel';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { PilotId } from '~/eds/src/types/PilotId';
import { UserAction } from '~/eds/src/types/UserAction';
import { RoleType } from '~/enums/Roles';
import { TableContextType } from '~/enums/TableContextType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { getStatusLabels } from '~/pages/super-admin/users/utils';
import { api } from '~/redux/api/createApi';
import { UserAttributes } from '~/redux/api/methods/roles/roles';
import { ParsedType } from '~/redux/api/transformers/utils';
import { coerceRtkqError } from '~/redux/api/utils';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';
import { testIsSuperAdmin } from '~/utils/user';

import { UsersFiltersType } from './types';
import { UsersFilters } from './UsersFilters';
import { UsersSearchBar } from './UsersSearchBar';

const current = {
  text: 'Users',
  pathname: RoutePathType.AdminUsers,
};

const COLUMNS = [
  'name',
  'email',
  'role',
  'departments',
  'job_title',
  'status',
  'last_login',
];
const WORKDAY_COLUMNS = ['beacon_id', 'upw_last_successful_sync'];

const STATUSES = {
  Active: 'success',
  Deactivated: 'inactive',
  Pending: 'warning',
};

const Page = () => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const statusLabels = getStatusLabels(intl);
  const { WORKDAY_ADMIN_MANAGED_TEXT } = getUsersConstans(intl);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isImportWizardVisible, setImportWizardVisible] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [resetPasswordVisible, setResetPassword] = useState(false);
  const [search, setSearch] = useState<Nullable<string>>(null);
  const [activeImportId, setActiveImportId] = useState<Nullable<Uuid>>(null);
  const { location, params } = useRouting();
  const query = new URLSearchParams(location.search);
  const { clientId: clientIdQueryParam } = params as { clientId: string };
  const currentUser = useCurrentUser();
  const isSuperAdmin = testIsSuperAdmin(currentUser);

  const {
    data: isSelfWorkdayUser = false,
    isLoading: isLoadingIsWorkdayClientUser,
  } = api.endpoints.getIsWorkdayUser.useQuery({}, { skip: isSuperAdmin });

  const { data: isWorkdayClient = false, isLoading: isLoadingIsWorkdayClient } =
    api.endpoints.getIsWorkdayClient.useQuery(
      { clientId: Number(clientIdQueryParam) },
      { skip: !isSuperAdmin },
    );

  const isWorkdayUser = isSuperAdmin ? isWorkdayClient : isSelfWorkdayUser;

  const rolesInitialState = query.get('role')
    ? [query.get('role') as PilotId]
    : [];

  const [filters, setFilters] = useState<UsersFiltersType>({
    status: ['Active', 'Pending'],
    roles: rolesInitialState,
  });
  const hasSelectedFilters = Object.keys(filters).length > 0 || search;

  const isEmailNotificationEnabled = useFlag(
    FlagType.ReactivateUsersEmailNotification,
  );

  const isLoadingUserCategory =
    isLoadingIsWorkdayClientUser || isLoadingIsWorkdayClient;
  const isRolodexDataSourceEnabled = useFlag(FlagType.RolodexAsUserDataSource);

  const { toast } = useToast();
  const [sendInvitationEmail] = api.endpoints.sendInvitationEmail.useMutation();
  const [resendReactivatedUserInvite] =
    api.endpoints.resendReactivatedUserInvite.useMutation();
  const [reactivateUser, reactivateUserResult] =
    api.endpoints.reactivateUser.useMutation();
  const {
    isError: isErrorReactivateUser,
    isLoading: isLoadingReactivateUser,
    isSuccess: isSuccessReactivateUser,
  } = reactivateUserResult;
  const [cancelUserInvite, cancelUserInviteResult] =
    api.endpoints.cancelUserInvite.useMutation();
  const {
    isError: isErrorCancelUserInvite,
    isLoading: isLoadingCancelUserInvite,
    isSuccess: isSuccessCancelUserInvite,
  } = cancelUserInviteResult;

  const clientId = clientIdQueryParam
    ? Number(clientIdQueryParam)
    : currentUser.client;

  const [selectedUser, setSelectedUser] =
    useState<Nullable<ParsedType<UserAttributes>>>(null);

  const nav = generateNav({
    current,
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId,
    },
    intl,
  });

  const defaultColumnOrder = useMemo(
    () => (isWorkdayUser ? [...COLUMNS, ...WORKDAY_COLUMNS] : COLUMNS),
    [isWorkdayUser],
  );

  const {
    tableSettings = {
      pageSize: 10,
      columnOrder: defaultColumnOrder,
    },
  } = useTableSettings(TableContextType.Users);

  const [getUsers, { isFetching, data }] =
    api.endpoints.getUsers.useLazyQuery();
  const results = data?.results ?? [];

  const [pageSize, setPageSize] = useState(tableSettings.pageSize);

  const customAttributesColumns = useMemo(
    () =>
      (data?.columns ?? []).map((column) => ({
        key: column.key,
        title: column.label,
        width: 'm',
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: get(user.customAttributeValues, column.key, ''),
        }),
      })),
    [data?.columns],
  );

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage({
          id: 'AdminUsersPage.userColumnTitle',
          defaultMessage: 'User',
          description: 'Column header for user information',
        }),
        width: 'l',
        cellType: 'user',
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          user: {
            id: 1,
            firstName: user.firstName,
            lastName: user.lastName,
          },
          mode: 'avatar-name',
        }),
      },
      {
        key: 'email',
        title: intl.formatMessage({
          id: 'AdminUsersPage.emailColumnTitle',
          defaultMessage: 'Email',
          description: 'Column header for user email',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.email,
        }),
      },
      {
        key: 'role',
        title: intl.formatMessage({
          id: 'AdminUsersPage.roleColumnTitle',
          defaultMessage: 'Role',
          description: 'Column header for user role',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.role,
        }),
      },
      {
        key: 'departments',
        title: intl.formatMessage({
          id: 'AdminUsersPage.departmentColumnTitle',
          defaultMessage: 'Department',
          description: 'Column header for user department',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.departments
            .map((department) => department.departmentName)
            .join(', '),
        }),
      },
      {
        key: 'job_title',
        title: intl.formatMessage({
          id: 'AdminUsersPage.jobTitleColumnTitle',
          defaultMessage: 'Job Title',
          description: 'Column header for user job title',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.jobTitle,
        }),
      },
      {
        key: 'status',
        title: intl.formatMessage({
          id: 'AdminUsersPage.statusColumnTitle',
          defaultMessage: 'Status',
          description: 'Column header for user status',
        }),
        cellType: 'chips',
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          chips: [
            { text: statusLabels[user.status], status: STATUSES[user.status] },
          ],
        }),
      },
      ...(isWorkdayUser
        ? [
            {
              key: 'beacon_id',
              title: intl.formatMessage({
                id: 'AdminUsersPage.beaconIdColumnTitle',
                defaultMessage: 'Beacon ID',
                description: 'Column header for beacon id',
              }),
              disableSortBy: true,
              mapCellProps: (user: ParsedType<UserAttributes>) => ({
                text: user.beaconId ?? '—',
              }),
            },
            {
              key: 'upw_last_successful_sync',
              title: intl.formatMessage({
                id: 'AdminUsersPage.lastSuccessfulSyncColumnTitle',
                defaultMessage: 'Last Successful Sync',
                description: 'Column header for last successful sync date',
              }),
              width: 'm',
              disableSortBy: true,
              cellType: 'datetime',
              mapCellProps: (user: ParsedType<UserAttributes>) => ({
                datetime: user.upwLastSuccessfulSync,
                format: 'short_datetime',
              }),
            },
          ]
        : []),
      {
        key: 'last_login',
        title: intl.formatMessage({
          id: 'AdminUsersPage.lastSignInColumnTitle',
          defaultMessage: 'Last sign in',
          description: 'Column header for user last sign in date',
        }),
        width: 'm',
        disableSortBy: true,
        cellType: 'datetime',
        mapCellProps: ({ lastLogin }: ParsedType<UserAttributes>): Object => {
          return {
            datetime: lastLogin,
            format: 'short_datetime',
          };
        },
      },
      ...customAttributesColumns,
    ],
    [customAttributesColumns, intl, isWorkdayUser, statusLabels],
  );

  const columnGroups = useMemo(() => {
    return [
      {
        name: 'default',
        label: intl.formatMessage({
          id: 'AdminUsersPage.defaultAttributesLabel',
          defaultMessage: 'Default Attributes',
          description: 'Label for default user attributes column group',
        }),
        columns: [
          'name',
          'email',
          'role',
          'departments',
          'job_title',
          'status',
          ...(isWorkdayUser ? WORKDAY_COLUMNS : []),
          'last_login',
        ],
      },
      {
        name: 'custom',
        label: intl.formatMessage({
          id: 'AdminUsersPage.customAttributesLabel',
          defaultMessage: 'Custom Attributes',
          description: 'Label for custom user attributes column group',
        }),
        columns: customAttributesColumns.map((column) => column.key),
      },
    ];
  }, [customAttributesColumns, intl, isWorkdayUser]);

  const getRowActions = useCallback(
    (rowUser: ParsedType<UserAttributes>) => {
      const isOwner =
        currentUser.role_uuid?.name === 'Owner' &&
        currentUser.role_uuid.role_type === RoleType.PreBuiltRole;

      return [
        {
          label: intl.formatMessage({
            id: 'AdminUsersPage.editUserAction',
            defaultMessage: 'Edit user',
            description: 'Row action to edit user information',
          }),
          onClick: async (user: ParsedType<UserAttributes>) => {
            setPanelVisible(true);
            setSelectedUser(user);
          },
          disabled: rowUser.role === 'Owner' && !(isOwner || isSuperAdmin),
          condition: (user: ParsedType<UserAttributes>) =>
            user.status !== 'Deactivated',
        },
        {
          label: intl.formatMessage({
            id: 'AdminUsersPage.deactivateUserAction',
            defaultMessage: 'Deactivate user',
            description: 'Row action to deactivate user',
          }),
          onClick: async (user: ParsedType<UserAttributes>): Promise<void> => {
            setDeactivateVisible(true);
            setSelectedUser(user);
          },
          icon: '',
          disabled:
            isWorkdayUser ||
            (rowUser.role === 'Owner' && !(isOwner || isSuperAdmin)),
          condition: (user: ParsedType<UserAttributes>) =>
            user.status === 'Active',
          tooltip: isWorkdayUser ? WORKDAY_ADMIN_MANAGED_TEXT : '',
        },
        {
          label: intl.formatMessage({
            id: 'AdminUsersPage.reactivateUserAction',
            defaultMessage: 'Reactivate user',
            description: 'Row action to reactivate user',
          }),
          onClick: async (user: ParsedType<UserAttributes>) => {
            trackSegment('submitReactivateUser', {
              id: user.id,
              user_id: currentUser.id,
              client_id: currentUser.client,
            });
            reactivateUser({
              userId: user.id,
              enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
            });
            setSelectedUser(user);
          },
          disabled: isWorkdayUser,
          icon: '',
          condition: (user: ParsedType<UserAttributes>) =>
            user.status === 'Deactivated',
          tooltip: isWorkdayUser ? WORKDAY_ADMIN_MANAGED_TEXT : '',
        },
        {
          label: intl.formatMessage({
            id: 'AdminUsersPage.resetPasswordAction',
            defaultMessage: 'Reset password',
            description: 'Row action to reset user password',
          }),
          onClick: async (user: ParsedType<UserAttributes>) => {
            setResetPassword(true);
            setSelectedUser(user);
          },
          icon: '',
          condition: (user: ParsedType<UserAttributes>) =>
            user.status === 'Active' && !user.ssoUser,
          disabled: isWorkdayUser,
          tooltip: isWorkdayUser ? WORKDAY_ADMIN_MANAGED_TEXT : '',
        },
        {
          label: intl.formatMessage({
            id: 'AdminUsersPage.cancelInviteAction',
            defaultMessage: 'Cancel invite',
            description: 'Row action to cancel user invite',
          }),
          onClick: (user: ParsedType<UserAttributes>) => {
            trackSegment('submitCancelUserInvite', {
              id: user.id,
              user_id: currentUser.id,
              client_id: currentUser.client,
            });
            cancelUserInvite({
              userId: user.id,
              enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
            });
            setSelectedUser(user);
          },
          icon: '',
          disabled:
            isWorkdayUser ||
            (rowUser.role === 'Owner' && !(isOwner || isSuperAdmin)),
          tooltip: isWorkdayUser ? WORKDAY_ADMIN_MANAGED_TEXT : '',
          condition: (user: ParsedType<UserAttributes>) =>
            user.status === 'Pending',
        },
        {
          label: intl.formatMessage({
            id: 'AdminUsersPage.sendInviteAction',
            defaultMessage: 'Send invite',
            description: 'Row action to send user invite',
          }),
          onClick: async (user: ParsedType<UserAttributes>) => {
            trackSegment('submitCancelUserInvite', {
              id: user.id,
              user_id: currentUser.id,
              client_id: currentUser.client,
            });
            setIsLoading(true);
            try {
              const resendRequest = isEmailNotificationEnabled
                ? resendReactivatedUserInvite
                : sendInvitationEmail;

              await resendRequest({
                userId: user.id,
                enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
              }).unwrap();

              toast({
                title: intl.formatMessage({
                  id: 'AdminUsersPage.inviteSentTitle',
                  defaultMessage: 'Invite sent',
                  description: 'Toast title when invite is sent successfully',
                }),
                message: intl.formatMessage(
                  {
                    id: 'AdminUsersPage.inviteSentMessage',
                    defaultMessage: 'User will receive an email at "{email}"',
                    description:
                      'Toast message when invite is sent successfully. Example: email = "user@example.com"',
                  },
                  {
                    email: user.email,
                  },
                ),
                status: 'success',
              });
            } catch (e: any) {
              const resp = e?.response || {};
              const msg = resp.data?.errors?.[0];
              toast({
                message:
                  msg?.title ||
                  intl.formatMessage({
                    id: 'AdminUsersPage.inviteErrorMessage',
                    defaultMessage:
                      'An error occurred while resending the user invite.',
                    description: 'Error message when invite sending fails',
                  }),
                status: 'danger',
              });
            } finally {
              setIsLoading(false);
            }
          },
          icon: '',
          disabled: isWorkdayUser,
          tooltip: isWorkdayUser ? WORKDAY_ADMIN_MANAGED_TEXT : '',
          condition: (user: ParsedType<UserAttributes>) =>
            user.status === 'Pending',
        },
      ];
    },
    [currentUser, isWorkdayUser],
  );

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        setPage(pageIndex);
      }
    },
    [page],
  );
  const onPageSizeChange = useCallback((pageSize: number) => {
    setPage(1);
    setPageSize(pageSize);
  }, []);

  const handleHidePanel = () => {
    setPanelVisible(false);
    setSelectedUser(null);
  };

  const handleHideDeactivate = () => {
    setDeactivateVisible(false);
    setSelectedUser(null);
  };

  const handleHideResetPassword = () => {
    setResetPassword(false);
    setSelectedUser(null);
  };

  const handleExportUsers = async () => {
    const timestamp = formatDate(new Date(), 'utc');
    const filename = `evisort-users-${timestamp}.xlsx`;
    setIsLoading(true);
    try {
      const response = await exportUsersExcelFile({
        clientId,
        onlyHeaders: false,
      });
      toast({
        message: intl.formatMessage({
          id: 'AdminUsersPage.fileDownloadSuccessMessage',
          defaultMessage: 'File downloaded successfully.',
          description: 'Success message when file download completes',
        }),
        status: 'success',
      });
      // Adding unkown due lacking types in the functions library
      saveAs(response as unknown as Blob, filename);
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'AdminUsersPage.exportErrorMessage',
          defaultMessage: 'Error exporting file',
          description: 'Error message when file export fails',
        }),
        status: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (searchValue: Nullable<string>) => {
    setPage(1);
    setSearch(searchValue);
    if (searchValue && searchValue.length > MIN_SEARCH_LENGTH) {
      getUsers({
        searchValue,
        page: 1,
        pageSize,
        clientId,
        roles: filters.roles ?? undefined,
        departments: filters.departments ?? undefined,
        status: filters.status ?? undefined,
        enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
      });
      trackSegment('searchTextUsers', {
        searchValue,
        user_id: currentUser.id,
        client_id: currentUser.client,
      });
    } else if (!searchValue) {
      getUsers({
        searchValue: undefined,
        page: 1,
        pageSize,
        clientId,
        roles: filters.roles ?? undefined,
        departments: filters.departments ?? undefined,
        status: filters.status ?? undefined,
        enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
      });
    }
  };

  useEffect(() => {
    if (isLoadingUserCategory) return;

    getUsers({
      searchValue: search ?? undefined,
      page,
      pageSize,
      clientId,
      roles: filters.roles ?? undefined,
      departments: filters.departments ?? undefined,
      status: filters.status ?? undefined,
      enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
    });
  }, [page, pageSize, filters, isLoadingUserCategory, isWorkdayUser]);

  useEffect(() => {
    if (isSuccessReactivateUser) {
      if (!isEmailNotificationEnabled) {
        sendInvitationEmail({
          userId: selectedUser!.id,
          enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
        });
      }
      toast({
        message: intl.formatMessage(
          {
            id: 'AdminUsersPage.userReactivatedMessage',
            defaultMessage: '"{firstName} {lastName}" reactivated.',
            description:
              'Success message when user is reactivated. Example: firstName = "John", lastName = "Doe"',
          },
          {
            firstName: selectedUser?.firstName,
            lastName: selectedUser?.lastName,
          },
        ),
        status: 'success',
      });
      setSelectedUser(null);
    }
  }, [isSuccessReactivateUser]);

  useEffect(() => {
    if (isSuccessCancelUserInvite) {
      toast({
        title: intl.formatMessage({
          id: 'AdminUsersPage.inviteCancelledTitle',
          defaultMessage: 'Invite canceled',
          description: 'Toast title when invite is canceled',
        }),
        message: intl.formatMessage({
          id: 'AdminUsersPage.inviteCancelledMessage',
          defaultMessage: "The user's activation link is no longer valid",
          description: 'Toast message when invite is canceled',
        }),
        status: 'success',
      });
      setSelectedUser(null);
    }
  }, [isSuccessCancelUserInvite]);

  useEffect(() => {
    if (isErrorReactivateUser) {
      const resp = coerceRtkqError(reactivateUserResult.error)?.response || {};
      const msg = resp.data?.errors?.[0];
      toast({
        message:
          msg?.title ||
          intl.formatMessage({
            id: 'AdminUsersPage.reactivateErrorMessage',
            defaultMessage: 'An error occurred while reactivating the user.',
            description: 'Error message when user reactivation fails',
          }),
        status: 'danger',
      });
      setSelectedUser(null);
    }
  }, [isErrorReactivateUser]);

  useEffect(() => {
    if (isErrorCancelUserInvite) {
      const resp =
        coerceRtkqError(cancelUserInviteResult.error)?.response || {};
      const msg = resp.data?.errors?.[0];
      toast({
        message:
          msg?.title ||
          intl.formatMessage({
            id: 'AdminUsersPage.cancelInviteErrorMessage',
            defaultMessage:
              'An error occurred while cancelling the user invite.',
            description: 'Error message when invite cancellation fails',
          }),
        status: 'danger',
      });
      setSelectedUser(null);
    }
  }, [isErrorCancelUserInvite]);

  const onImport = (importId: Uuid) => {
    setActiveImportId(importId);
  };

  return (
    <PageLayout
      nav={nav}
      title={intl.formatMessage({
        id: 'AdminUsersPage.pageTitle',
        defaultMessage: 'Users',
        description: 'Page title for users administration',
      })}
      description={intl.formatMessage({
        id: 'AdminUsersPage.pageDescription',
        defaultMessage:
          'Create users or manage user information, roles, and permissions.',
        description: 'Page description for users administration',
      })}
      actions={[
        {
          level: 'secondary',
          text: intl.formatMessage({
            id: 'AdminUsersPage.exportUsersButtonText',
            defaultMessage: 'Export Users',
            description: 'Button text for exporting users',
          }),
          isLoading: isLoading,
          onClick: () => {
            trackSegment('exportUsers', {
              user_id: currentUser.id,
              client_id: clientId,
            });
            handleExportUsers();
          },
        },
        {
          disabled: isWorkdayUser,
          id: OnboardingIdType.ImportNewUser,
          isLoading: isLoadingIsWorkdayClientUser || isLoadingIsWorkdayClient,
          level: 'secondary' as UserAction['level'],
          text: intl.formatMessage({
            id: 'AdminUsersPage.importUsersButtonText',
            defaultMessage: 'Import Users',
            description: 'Button text for importing users',
          }),
          tooltip: isWorkdayUser ? WORKDAY_ADMIN_MANAGED_TEXT : '',
          onClick: () => {
            trackSegment('openImportUsersWizard', {
              user_id: currentUser.id,
              client_id: currentUser.client,
            });
            setImportWizardVisible(true);
          },
        },
        {
          disabled: isWorkdayUser,
          id: OnboardingIdType.AddNewUser,
          isLoading: isLoadingIsWorkdayClientUser || isLoadingIsWorkdayClient,
          level: 'primary' as UserAction['level'],
          text: intl.formatMessage({
            id: 'AdminUsersPage.newUserButtonText',
            defaultMessage: 'New User',
            description: 'Button text for creating new user',
          }),
          tooltip: isWorkdayUser ? WORKDAY_ADMIN_MANAGED_TEXT : '',
          onClick: () => {
            setPanelVisible(true);
            trackSegment('openCreateUserPanel', {
              user_id: currentUser.id,
              client_id: currentUser.client,
            });
          },
        },
      ]}
    >
      <Layout justify="space-between">
        <Layout flex="1" mr={3}>
          <UsersSearchBar onSearch={handleSearch} />
        </Layout>
        <Layout spacing={1} align="center">
          <Layout alignSelf="center">
            <Text>
              <FormattedMessage
                id="AdminUsersPage.filtersLabel"
                defaultMessage="Filters:"
                description="Label for filters section"
              />
            </Text>
          </Layout>
          <UsersFilters
            clientId={clientId}
            filters={filters}
            onChangeFilter={setFilters}
          />
        </Layout>
      </Layout>
      <Box py={1} spacing>
        <ImportUserStatus activeImportId={activeImportId} clientId={clientId} />
      </Box>
      <Layout direction="column" spacing={8} mt={6}>
        <PersistedTable
          name={TableContextType.Users}
          data={results}
          options={{
            enableSelectRows: false,
            enablePageSizeSelect: true,
            enableExportXlsx: false,
            enableStickyHeader: true,
            stickyHeaderOffset: NAV_HEIGHT,
          }}
          actions={[
            {
              icon: 'file-spreadsheet',
              label: intl.formatMessage({
                id: 'AdminUsersPage.exportUsersActionLabel',
                defaultMessage: 'Export Users',
                description: 'Table action label for exporting users',
              }),
              onClick: () => {
                trackSegment('exportUsers', {
                  user_id: currentUser.id,
                  client_id: clientId,
                });
                handleExportUsers();
              },
            },
          ]}
          getRowActions={getRowActions}
          state={{
            pageIndex: page,
            pageSize,
            columnOrder: tableSettings.columnOrder ?? defaultColumnOrder,
          }}
          isLoading={
            isFetching ||
            isLoadingReactivateUser ||
            isLoadingCancelUserInvite ||
            isLoading ||
            isLoadingUserCategory
          }
          columns={columns}
          columnGroups={columnGroups}
          context={TableContextType.Users}
          totalCount={data?.total}
          onPaginate={onPaginate}
          onPageSizeChange={onPageSizeChange}
        />
        {data?.results?.length === 0 && hasSelectedFilters && (
          <Layout align="center" justify="center">
            <ContentContainer
              placeholderContent={{
                title: intl.formatMessage({
                  id: 'AdminUsersPage.noUsersMatchedTitle',
                  defaultMessage: 'No Users Matched this Search',
                  description: 'Title when no users match search criteria',
                }),
                description: intl.formatMessage({
                  id: 'AdminUsersPage.noUsersMatchedDescription',
                  defaultMessage:
                    'Try another search, or use the filters next to the search bar to find a user by role, department, and more.',
                  description:
                    'Description when no users match search criteria',
                }),
              }}
            />
          </Layout>
        )}
      </Layout>
      <UserFormPanel
        clientId={clientId}
        isWorkdayUser={isWorkdayUser}
        visible={panelVisible}
        onHide={handleHidePanel}
        user={selectedUser}
      />
      <DeactivateUserModal
        isWorkdayUser={isWorkdayUser}
        visible={deactivateVisible}
        onHide={handleHideDeactivate}
        user={selectedUser}
      />
      <ResetUserPasswordModal
        isWorkdayUser={isWorkdayUser}
        visible={resetPasswordVisible}
        onHide={handleHideResetPassword}
        user={selectedUser}
      />
      {isImportWizardVisible && (
        <ImportUsersWizard
          clientId={clientId ? Number(clientId) : currentUser.client}
          onImport={onImport}
          onClose={() => setImportWizardVisible(false)}
        />
      )}
    </PageLayout>
  );
};

export default Page;
